import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const getAuthToken = () => {
  try {
    const authToken = localStorage.getItem('auth_token');
    return authToken || '';
  } catch (error) {
    return '';
  }
};

const api = axios.create({
  baseURL: `${API_BASE_URL}/api/gpu`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export interface GPUSpec {
  id: string;
  name: string;
  gpuType: string;
  gpuCount: number;
  specifications: {
    cpu: string;
    cpuCores: number;
    ram: string;
    ramGB: number;
    storage: string;
    storageGB: number;
    bandwidth: string;
    vram: string;
    cudaCores: number;
    tensorCores: number;
  };
  regions: string[];
  pricing: {
    hourly: number;
    monthly: number;
  };
  useCases: string[];
}

export interface GPUInstance {
  id: string;
  userId: string;
  leasewebId?: string;
  name: string;
  gpuType: string;
  gpuCount: number;
  status: 'running' | 'stopped' | 'provisioning' | 'stopping' | 'starting' | 'terminated' | 'error';
  region: string;
  specifications: {
    cpu: string;
    cpuCores: number;
    ram: string;
    ramGB: number;
    storage: string;
    storageGB: number;
    bandwidth: string;
    vram: string;
    cudaCores: number;
    tensorCores: number;
  };
  network: {
    publicIP?: string;
    privateIP?: string;
    ipv6?: string;
  };
  ssh: {
    keyName: string;
    username: string;
    port: number;
  };
  billing: {
    pricePerHour: number;
    pricePerMonth: number;
    currentMonthCost: number;
    billingCycle: 'hourly' | 'monthly';
  };
  metadata: {
    operatingSystem: string;
    osVersion: string;
    installedSoftware: string[];
    tags: string[];
    description?: string;
    purpose?: string;
  };
  uptime: {
    startedAt?: Date;
    totalUptimeHours: number;
  };
  createdAt: Date;
  lastUpdatedAt: Date;
  terminatedAt?: Date;
  uptimeString?: string;
  currentCost?: number;
}

export interface GPUStats {
  total: number;
  running: number;
  stopped: number;
  provisioning: number;
  totalMonthlyCost: number;
  totalCurrentCost: number;
}

export interface HealthCheck {
  configured: boolean;
  operational: boolean;
  message: string;
  catalogAvailable?: boolean;
  catalogSize?: number;
  apiUrl?: string;
  fallbackAvailable?: boolean;
}


/**
 * GPU API Service
 */
export const gpuApi = {
  /**
   * Get GPU catalog (available configurations)
   */
  async getCatalog(): Promise<{ data: GPUSpec[]; meta: any }> {
    const response = await api.get('/catalog');
    return response.data;
  },

  /**
   * Get available regions
   */
  async getRegions(): Promise<string[]> {
    const response = await api.get('/catalog/regions');
    return response.data.data;
  },

  /**
   * Get GPU catalog by type
   */
  async getCatalogByType(gpuType: string): Promise<GPUSpec[]> {
    const response = await api.get(`/catalog/type/${gpuType}`);
    return response.data.data;
  },

  /**
   * Get all GPU instances
   */
  async getInstances(): Promise<GPUInstance[]> {
    const response = await api.get('/instances');
    return response.data.data;
  },

  /**
   * Get GPU instance statistics
   */
  async getStats(): Promise<GPUStats> {
    const response = await api.get('/instances/stats');
    return response.data.data;
  },

  /**
   * Get specific instance details
   */
  async getInstance(instanceId: string): Promise<GPUInstance> {
    const response = await api.get(`/instances/${instanceId}`);
    return response.data.data;
  },

  /**
   * Create a new GPU instance
   */
  async createInstance(data: {
    configId: string;
    name: string;
    region: string;
    gpuType: string;
    gpuCount: number;
    sshKeyName?: string;
  }): Promise<GPUInstance> {
    const response = await api.post('/instances', data);
    return response.data.data;
  },

  /**
   * Create a Stripe Checkout Session for a GPU contract purchase.
   * Provisioning only happens after payment confirms — redirect the browser
   * to the returned checkout.url, then call confirmCheckout() once Stripe
   * redirects back.
   */
  async createCheckout(data: {
    configId: string;
    name: string;
    region: string;
    sshKeyName?: string;
    contractTerm: 1 | 3 | 6 | 12;
  }): Promise<{ checkout: { url: string; session_id: string; key: string } }> {
    const response = await api.post('/checkout', data);
    return response.data;
  },

  /**
   * FE-driven confirm after landing back on the success URL. Idempotent —
   * the Stripe webhook may also complete this same order.
   */
  async confirmCheckout(sessionId: string): Promise<{ paid: boolean; instance?: GPUInstance }> {
    const response = await api.post('/checkout/confirm', { sessionId });
    return response.data;
  },

  /**
   * Start a stopped instance
   */
  async startInstance(instanceId: string): Promise<GPUInstance> {
    const response = await api.post(`/instances/${instanceId}/start`);
    return response.data.data;
  },

  /**
   * Stop a running instance
   */
  async stopInstance(instanceId: string): Promise<GPUInstance> {
    const response = await api.post(`/instances/${instanceId}/stop`);
    return response.data.data;
  },

  /**
   * Reboot an instance
   */
  async rebootInstance(instanceId: string): Promise<GPUInstance> {
    const response = await api.post(`/instances/${instanceId}/reboot`);
    return response.data.data;
  },

  /**
   * Terminate an instance
   */
  async terminateInstance(instanceId: string, reason?: string): Promise<GPUInstance> {
    const response = await api.delete(`/instances/${instanceId}`, {
      data: { reason }
    });
    return response.data.data;
  },

  /**
   * Health check for GPU service
   */
  async healthCheck(): Promise<HealthCheck> {
    const response = await api.get('/health');
    return response.data.data;
  },
};

export default gpuApi;
